import { useContext, useState, useRef, useEffect } from "react";
import mammoth from "mammoth";
import AlertService from "../../../services/AlertService";
import SupportService from "../../../services/SupportService";
import AuthContext from "../../../auth/AuthContext";
import DocumentViewer from "./Viewer";
import { Sidebar } from "primereact/sidebar";

const Attachment = ({ no, attachment, isComment }) => {
  const [loading, setLoading] = useState(false);
  const authContext = useContext(AuthContext);
  const [contentType, setContentType] = useState(null);
  const [pdfData, setPDFData] = useState(null);
  const [showPDF, setShowPDF] = useState(false);
  const [showDoc, setShowDoc] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [htmlContent, setHtmlContent] = useState(null);
  const [visible, setVisible] = useState(false);
  const viewerRef = useRef();

  const fromComment = !!isComment;

const downloadFile = async () => {
    setLoading(true);
    setShowPDF(false);
    setShowDoc(false);
    setShowImage(false);
    setPDFData(null);
    setHtmlContent(null);

    try {
      const data = fromComment
        ? await SupportService.GetCommentFile(attachment.id, authContext)
        : await SupportService.GetFile(attachment.id, authContext);

      if (data.isSuccess) {
        const ct = data.data.contentType;
        setContentType(ct);

        if (ct === "application/pdf") {
          setShowPDF(true);
          setPDFData(data.data.filePath);
          setVisible(true);
        } else if (ct === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
          setShowDoc(true);
          try {
            const byteCharacters = atob(data.data.filePath);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: ct });

            const reader = new FileReader();
            reader.onload = async (e) => {
              const arrayBuffer = reader.result;
              const result = await mammoth.convertToHtml({ arrayBuffer });
              setHtmlContent(result.value);
              setVisible(true);
            };
            reader.readAsArrayBuffer(blob);
          } catch {
            AlertService.error("Failed to load document preview.");
          }
        } else {
          setShowImage(true);
          setPDFData(data.data.filePath);
          setVisible(true);
        }
      } else {
        AlertService.error(data.errorMessage || "Failed to fetch attachment.");
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      setLoading(false);
    }
  };

  // When Sidebar opens with PDF, trigger fullscreen
  useEffect(() => {
    if (visible && showPDF && viewerRef.current) {
      // Small delay to ensure viewer loads
      setTimeout(() => {
        if (viewerRef.current.openFullScreen) {
          viewerRef.current.openFullScreen();
        }
      }, 500);
    }
  }, [visible, showPDF]);

  return (
    <>
      <div className="attachment me-3 " style={{ userSelect: "none" }}>
        <span>{no}. </span>
        <button
          type="button"
          className={`btn btn-attachment p-2 align-baseline ${loading ? "disabled" : ""}`}
          onClick={downloadFile}
          title="Click to preview file"
          disabled={loading}
          style={{
            cursor: loading ? "default" : "pointer"
          }}
        >
          {attachment.fileName}
          {loading ? " ..." : ""}
        </button>
      </div>

      <Sidebar visible={visible} onHide={() => setVisible(false)} fullScreen>
        {showPDF && pdfData && (
          <DocumentViewer ref={viewerRef} src={pdfData} contentType={contentType} title={attachment.fileName} />
        )}
        {showDoc && htmlContent && (
          <div
            className="border rounded p-3 scroll-no-buttons"
            style={{ maxHeight: "90vh", overflowY: "auto", background: "#fafafa" }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        )}
        {showImage && pdfData && (
          <img
            alt={attachment.fileName}
            className="scroll-no-buttons"
            src={pdfData}
            style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: 4 }}
          />
        )}
      </Sidebar>
    </>
  );
};

export default Attachment;
